import React, { useEffect, useState, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Phone, Clock, Globe, AlertTriangle, ArrowRight, Settings, GraduationCap } from 'lucide-react';
import { fetchTutorCallInfo } from '@/api/chat';
import LanguageSelectionModal, { LANGUAGES } from './LanguageSelectionModal';
import { usePreferences } from '@/context/PreferencesContext';
import { useSubjectChapter } from '@/context/SubjectChapterContext';
import { cn } from '@/lib/utils';

const CallConfirmationModal = ({
    open,
    onClose,
    subjectName,
    subjectId,
    chapterId,
    chapterName,
    allowFallbackTutor = false,
    title = 'Call AI Tutor',
    confirmLabel = 'Start Call',
    mode = 'call',
    contextLabel = '',
    autoStartWhenReady = false,
    onConfirm,
    onSeeSubscriptions
}) => {
    const { theme, updatePreferences } = usePreferences();
    const { selectedSubject, selectedChapter } = useSubjectChapter();
    const [loading, setLoading] = useState(true);
    const [tutorInfo, setTutorInfo] = useState(null);
    const [error, setError] = useState(null);
    const [showLangSelect, setShowLangSelect] = useState(false);
    const wasAutoOpenedRef = useRef(false);
    const autoStartedRef = useRef(false);
    const isDark = theme !== 'light';
    const isGuest = localStorage.getItem('is_guest_session') === 'true';

    useEffect(() => {
        if (open && (subjectName || subjectId || allowFallbackTutor)) {
            loadTutorInfo();
        } else if (!open) {
            autoStartedRef.current = false;
            wasAutoOpenedRef.current = false;
            setShowLangSelect(false);
        }
    }, [open, subjectName, subjectId, chapterId, allowFallbackTutor]);

    const canAutoStart = (data) => {
        if (!autoStartWhenReady || autoStartedRef.current || !data || data.error) return false;
        if (data.minutes_left <= 0) return false;
        if (!data.preferred_languages || data.preferred_languages.length === 0) return false;
        return true;
    };

    const loadTutorInfo = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchTutorCallInfo(null, subjectName, {
                subjectId,
                chapterId,
                allowFallbackTutor,
            });
            setTutorInfo(data);
            if (canAutoStart(data)) {
                autoStartedRef.current = true;
                onConfirm?.(data?.tutor, data);
                return;
            }
            if (!isGuest && (!data?.preferred_languages || data.preferred_languages.length === 0) && !data.error) {
                setShowLangSelect(true);
                wasAutoOpenedRef.current = true;
            }
        } catch (err) {
            console.error('Failed to load tutor call info:', err);
            setError(err.response?.data?.error || 'AI Tutor details not available for this subject.');
        } finally {
            setLoading(false);
        }
    };

    const handleLanguageClick = async (clickedCode) => {
        if (isGuest) return;
        const currentLangs = tutorInfo?.preferred_languages || [];
        const isPrimary = currentLangs[0] === clickedCode;
        if (isPrimary) return;

        const newLangs = [clickedCode, ...currentLangs.filter(code => code !== clickedCode)];

        // Optimistic UI update
        setTutorInfo(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                preferred_languages: newLangs,
            };
        });

        try {
            await updatePreferences({ preferred_languages: newLangs });
            // Sync with server to get updated details
            const data = await fetchTutorCallInfo(null, subjectName, {
                subjectId,
                chapterId,
                allowFallbackTutor,
            });
            setTutorInfo(data);
        } catch (err) {
            console.error('Failed to update primary language:', err);
            // Revert state if backend call failed
            loadTutorInfo();
        }
    };

    if (!open) return null;

    if (autoStartWhenReady && loading && !showLangSelect) return null;

    return (
        <>
            <Dialog open={open && !showLangSelect} onOpenChange={(val) => { if (!val) onClose(); }}>
                <DialogContent className={cn(
                    "sm:max-w-[460px] border border-[hsl(var(--card-border))] bg-[hsl(var(--card-bg))] text-foreground shadow-2xl p-0 overflow-hidden rounded-3xl flex flex-col",
                    "max-sm:fixed max-sm:inset-x-4 max-sm:top-[calc(3.5rem+env(safe-area-inset-top,0px)+0.75rem)] max-sm:bottom-[calc(4rem+env(safe-area-inset-bottom,0px)+0.75rem)] max-sm:translate-x-0 max-sm:translate-y-0 max-sm:max-h-none sm:max-h-[90vh]"
                )}>
                    <DialogHeader className="px-6 py-4 max-sm:py-2 text-center border-b border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))] flex flex-col items-center justify-center relative">
                        <div className="h-14 w-14 rounded-full flex items-center justify-center mb-2 max-sm:mb-0 shadow-md bg-gradient-to-br from-[hsl(var(--green-bright))]/15 to-[hsl(var(--teal-vivid))]/15 text-[hsl(var(--teal-vivid))] border border-[hsl(var(--teal-vivid))]/20">
                            {mode === 'teach'
                                ? <GraduationCap className="w-7 h-7 animate-pulse" />
                                : <Phone className="w-7 h-7 animate-pulse" />}
                        </div>
                        <DialogTitle className="text-xl font-bold tracking-tight text-foreground">
                            {title}
                        </DialogTitle>
                        {(selectedSubject || subjectName || contextLabel) && (
                            <div className="flex justify-center mt-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold border bg-[hsl(var(--primary))]/10 border-[hsl(var(--primary))]/30 text-[hsl(var(--teal-vivid))]">
                                    {contextLabel || selectedSubject || subjectName}
                                    {(chapterName || selectedChapter) && (
                                        <>
                                            <span className="mx-1 opacity-60">•</span>
                                            {chapterName || selectedChapter}
                                        </>
                                    )}
                                </span>
                            </div>
                        )}
                    </DialogHeader>

                    <div className="p-6 space-y-6 overflow-y-auto min-h-0 flex-1">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-10 space-y-3">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--primary))]"></div>
                                <span className="text-sm text-muted-foreground">Fetching tutor info...</span>
                            </div>
                        ) : error ? (
                            <div className="flex flex-col items-center text-center py-6 space-y-3">
                                <div className="p-3 rounded-full bg-red-500/10 text-red-400">
                                    <AlertTriangle className="w-6 h-6" />
                                </div>
                                <div className="text-sm font-semibold text-foreground">{error}</div>
                                <Button variant="ghost" onClick={onClose} className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))]">
                                    Go Back
                                </Button>
                            </div>
                        ) : (
                            <>
                                {/* Tutor Profile Card */}
                                <div className="flex items-center gap-4 p-4 rounded-2xl border border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))]">
                                    <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border border-[hsl(var(--card-border))] bg-[hsl(var(--muted))]">
                                        {tutorInfo?.tutor?.avatar_url ? (
                                            <img src={tutorInfo.tutor.avatar_url} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-xl font-bold bg-[hsl(var(--primary))] text-white">
                                                {tutorInfo?.tutor?.full_name ? tutorInfo.tutor.full_name[0] : '?'}
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="text-base font-bold leading-tight text-foreground">
                                            {tutorInfo?.tutor?.full_name || 'AI Tutor'}
                                            <span className="inline-block text-[8px] font-bold border px-1.5 py-0.2 rounded-full align-super ml-1.5 text-[hsl(var(--teal-vivid))] border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10">AI</span>
                                        </h3>
                                        <p className="text-xs font-semibold text-[hsl(var(--teal-vivid))]">{tutorInfo?.tutor?.subject_name || subjectName} Tutor</p>
                                    </div>
                                </div>

                                {/* Quota Warnings */}
                                {tutorInfo?.minutes_left <= 0 ? (
                                    <div className="p-3.5 rounded-xl border flex items-start gap-2.5 text-xs leading-normal bg-red-500/10 border-red-500/25 text-red-500 dark:text-red-400">
                                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 animate-pulse" />
                                        <div className="flex-1 flex flex-col gap-2">
                                            <div>
                                                <span className="font-bold block mb-0.5">Monthly Quota Exceeded</span>
                                                You have used your entire {tutorInfo?.monthly_quota_minutes || 10}-minute monthly limit. Upgrade your subscription or wait for your subscription cycle to reset the limit.
                                            </div>
                                            <button
                                                type="button"
                                                onClick={onSeeSubscriptions}
                                                className="text-left font-bold underline transition w-fit text-red-600 dark:text-red-300 hover:opacity-80"
                                            >
                                                See Subscriptions &rarr;
                                            </button>
                                        </div>
                                    </div>
                                ) : tutorInfo?.minutes_left <= 2 ? (
                                    <div className="p-3.5 rounded-xl border flex items-start gap-2.5 text-xs leading-normal bg-amber-500/10 border-amber-500/25 text-amber-600 dark:text-amber-400">
                                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                        <div className="flex-1 flex flex-col gap-2">
                                            <div>
                                                <span className="font-bold block mb-0.5">Low Quota Alert</span>
                                                You have less than 2 minutes of voice calls left this month. The call will automatically disconnect when this time is exhausted.
                                            </div>
                                            <button
                                                type="button"
                                                onClick={onSeeSubscriptions}
                                                className="text-left font-bold underline transition w-fit text-amber-700 dark:text-amber-300 hover:opacity-80"
                                            >
                                                See Subscriptions &rarr;
                                            </button>
                                        </div>
                                    </div>
                                ) : null}

                                {/* Languages Matching Info */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Talk In</span>
                                        {!isGuest && (
                                            <button
                                                type="button"
                                                onClick={() => setShowLangSelect(true)}
                                                className="text-xs font-semibold text-[hsl(var(--teal-vivid))] hover:opacity-80 flex items-center gap-1 transition"
                                            >
                                                <Settings className="w-3.5 h-3.5" /> Configure
                                            </button>
                                        )}
                                    </div>

                                    {tutorInfo?.matched_languages && tutorInfo.matched_languages.length > 0 ? (
                                        <div className="flex flex-wrap gap-1.5 p-3 rounded-xl border border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))]">
                                            {[...tutorInfo.matched_languages]
                                                .sort((a, b) => {
                                                    const idxA = LANGUAGES.findIndex(l => l.code === a);
                                                    const idxB = LANGUAGES.findIndex(l => l.code === b);
                                                    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
                                                })
                                                .map(code => {
                                                    const isPrimary = tutorInfo.preferred_languages && tutorInfo.preferred_languages[0] === code;
                                                    const lang = LANGUAGES.find(l => l.code === code);
                                                    const name = lang ? lang.name : code;
                                                    return (
                                                        <button
                                                            key={code}
                                                            type="button"
                                                            disabled={isGuest || isPrimary}
                                                            onClick={() => handleLanguageClick(code)}
                                                            className={cn(
                                                                "inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all duration-200",
                                                                isPrimary
                                                                    ? 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--teal-vivid))] border-[hsl(var(--primary))]/30 font-bold shadow-sm'
                                                                    : isGuest
                                                                        ? 'bg-[hsl(var(--muted))] text-muted-foreground border-[hsl(var(--card-border))] opacity-70 cursor-not-allowed'
                                                                        : 'bg-[hsl(var(--card-bg))] text-foreground border-[hsl(var(--card-border))] hover:border-[hsl(var(--primary))]/40 hover:text-[hsl(var(--teal-vivid))] cursor-pointer'
                                                            )}
                                                        >
                                                            <Globe className="w-3 h-3" />
                                                            {name}
                                                            {isPrimary && (
                                                                <span className="text-[9px] bg-[hsl(var(--primary))] text-white px-1 py-0.2 rounded font-black tracking-wider uppercase scale-90 ml-1">Primary</span>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                        </div>
                                    ) : !tutorInfo?.preferred_languages || tutorInfo.preferred_languages.length === 0 ? (
                                        <div className="p-4 rounded-xl space-y-2.5 border border-amber-500/25 bg-amber-500/10">
                                            <div className="flex items-start gap-2.5 text-xs leading-normal text-amber-600 dark:text-amber-400">
                                                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                                <span>
                                                    You must configure your preferred languages before you can start the call.
                                                </span>
                                            </div>
                                            {!isGuest && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    onClick={() => setShowLangSelect(true)}
                                                    className="w-full h-8 text-xs font-semibold border-amber-500/30 text-amber-600 dark:text-amber-400 bg-transparent hover:bg-amber-500 hover:text-white"
                                                >
                                                    Configure Preferred Languages
                                                </Button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="p-4 rounded-xl space-y-2.5 border border-amber-500/25 bg-amber-500/10">
                                            <div className="flex items-start gap-2.5 text-xs leading-normal text-amber-600 dark:text-amber-400">
                                                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                                <span>
                                                    You haven't set preferred languages that this tutor supports. The tutor will speak in English.
                                                </span>
                                            </div>
                                            {!isGuest && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    onClick={() => setShowLangSelect(true)}
                                                    className="w-full h-8 text-xs font-semibold border-amber-500/30 text-amber-600 dark:text-amber-400 bg-transparent hover:bg-amber-500 hover:text-white"
                                                >
                                                    Configure Preferred Languages
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <DialogFooter className="p-4 border-t border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))] flex flex-col sm:flex-row gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="w-full sm:w-auto border-[hsl(var(--card-border))] text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))]"
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        {tutorInfo?.minutes_left <= 0 ? (
                            <Button
                                type="button"
                                onClick={onSeeSubscriptions}
                                className="w-full sm:w-auto ml-auto bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-95 text-white font-semibold px-8 shadow-lg shadow-emerald-500/20"
                            >
                                See Subscriptions
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                onClick={() => onConfirm(tutorInfo?.tutor, tutorInfo)}
                                disabled={loading || error || (tutorInfo && (!tutorInfo.preferred_languages || tutorInfo.preferred_languages.length === 0))}
                                className="w-full sm:w-auto ml-auto bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-95 text-white font-semibold px-8 shadow-lg shadow-emerald-500/20 disabled:opacity-50"
                            >
                                {confirmLabel} <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <LanguageSelectionModal
                open={showLangSelect}
                onClose={() => {
                    setShowLangSelect(false);
                    if (wasAutoOpenedRef.current) {
                        onClose(); // Close parent CallConfirmationModal as well if auto-opened
                    }
                }}
                onSaveSuccess={() => {
                    wasAutoOpenedRef.current = false; // Reset auto-opened state
                    loadTutorInfo();
                }}
            />
        </>
    );
};

export default CallConfirmationModal;
